<template>
  <Navigation with-exit @back="send('BACK')" @exit="send('EXIT')" />
  <Title>{{ i18n(translations.title) }}</Title>

  <div class="space-y-12">
    <slot name="before-form" />

    <RevForm :initial-values :validate="validate()" @submit="handleSubmit">
      <template #default="{ values, errors }">
        <div class="space-y-24">
          <RevInputText
            v-if="isPartialRefundAllowed"
            id="refundAmount"
            v-model="values.refundAmount"
            :disabled="isLoading"
            :error="errors.refundAmount"
            :format="
              (amount) => formatAmount(amount, currencyMinimumFractionDigits)
            "
            :label="i18n(translations.inputAmount)"
          />

          <div
            class="flex items-center gap-16"
            :class="[
              { 'mt-12': !isPartialRefundAllowed },
              { 'mt-24': isPartialRefundAllowed },
            ]"
          >
            <slot />
            <RevButton
              :disabled="isLoading"
              :loading="isLoading"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.refundButton) }}
            </RevButton>
          </div>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import type { Price } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import type { MachineSend } from '../../../ActionBar.machine.type'
import Navigation from '../../Navigation/Navigation.vue'
import Title from '../../Title/Title.vue'
import {
  formatAmount,
  isValidNumber,
  numberGreaterThan,
  numberLowerThan,
} from '../RefundFlow.utils'

import translations from './AmountStep.translations'

const i18n = useI18n()
const currentLocale = useI18nLocale()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const $httpFetch = useHttpFetch()
const orderlinePublicId = useOrderlinePublicIdFromRoute()

const emit = defineEmits(['done', 'save-partial-refund-amount'])

const { partialRefundAmount, maxRefundPrice, isPartialRefundAllowed, send } =
  defineProps<{
    partialRefundAmount?: string
    maxRefundPrice: Price
    isPartialRefundAllowed: boolean
    send: MachineSend
  }>()

const currencyMinimumFractionDigits = computed(() => {
  const options = Intl.NumberFormat(toBcp47Locale(currentLocale), {
    style: 'currency',
    currency: maxRefundPrice.currency,
  }).resolvedOptions()

  return options.minimumFractionDigits
})

const isLoading = ref(false)

type Values = {
  refundAmount: string
}

const initialValues = reactive<Values>({
  refundAmount: partialRefundAmount || maxRefundPrice.amount,
})

const MIN_REFUNDABLE_AMOUNT = '1'

const validate = () => {
  const minRefundPrice = {
    amount: MIN_REFUNDABLE_AMOUNT,
    currency: maxRefundPrice.currency,
  }

  return makeValidate<Values>({
    refundAmount: [
      required(i18n(translations.isRequired)),
      isValidNumber<Values>(
        i18n(translations.shouldBeLowerThan, {
          maxRefundablePrice: i18n.price(maxRefundPrice),
        }),
      ),
      numberLowerThan<Values>(
        maxRefundPrice.amount,
        i18n(translations.shouldBeLowerThan, {
          maxRefundablePrice: i18n.price(maxRefundPrice),
        }),
      ),
      numberGreaterThan<Values>(
        MIN_REFUNDABLE_AMOUNT,
        i18n(translations.shouldBeGreaterThan, {
          minRefundablePrice: i18n.price(minRefundPrice),
        }),
      ),
    ],
  })
}

const handleSubmit = async (values: Values) => {
  isLoading.value = true

  const { refundAmount } = values
  const formattedAmount = refundAmount.replace(',', '.')
  const isFullRefund =
    parseFloat(formattedAmount) === parseFloat(maxRefundPrice.amount)

  try {
    if (isFullRefund) {
      await $httpFetch(
        '/bm/customer_request/orderlines/:orderlinePublicId/refund',
        {
          method: 'POST',
          pathParams: {
            orderlinePublicId: orderlinePublicId.value,
          },
          body: {
            monetaryAmount: {
              amount: formattedAmount,
              currency: maxRefundPrice.currency,
            },
          },
        },
      )

      openSuccessToast()

      emit('done')
    } else {
      emit('save-partial-refund-amount', formattedAmount)
      send('PARTIAL_REFUND_INITIATED')
    }
  } catch (err) {
    openErrorToast({
      title: i18n(translations.errorToastTitle),
      content: i18n(translations.errorToastLabel),
    })

    const error = err as Error
    logger.error(error.message, {
      error,
      owners: ['bot-squad-seller-care-front'],
    })
  } finally {
    isLoading.value = false
  }
}
</script>
